/**
 * Custom Handsontable Renderers for ConfigurableBaseExplorer
 *
 * These renderers integrate HOT with the field transformer system
 * and provide action buttons in cells.
 */

import Handsontable from 'handsontable';
import { FieldTransformer, CustomAction } from '../types/baseExplorerConfig';

/**
 * Creates a renderer that applies field transformers to cell values
 * @param transformer - The field transformer to apply
 * @param getEntity - Function to get the full entity from row index
 */
export const createTransformerRenderer = (
  transformer: FieldTransformer | undefined,
  getEntity: (row: number) => any
) => {
  return function(
    this: any,
    instance: Handsontable.Core,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: any,
    cellProperties: Handsontable.CellProperties
  ): HTMLTableCellElement {
    // Start with default text renderer
    Handsontable.renderers.TextRenderer.call(this, instance, td, row, col, prop, value, cellProperties);

    if (transformer && value !== null && value !== undefined) {
      const entity = getEntity(row);
      try {
        const transformed = transformer.transform(value, entity);
        // Handle string and non-string transformed values
        td.innerHTML = String(transformed ?? '');
      } catch (error) {
        console.error('[HOT] Transform error:', error);
        td.innerHTML = String(value ?? '');
      }
    } else if (value === null || value === undefined) {
      td.innerHTML = '';
    }

    return td;
  };
};

/**
 * Options for creating an actions column renderer
 */
export interface ActionsRendererOptions {
  entityName: string;
  allowUpdate: boolean;
  allowDelete: boolean;
  onView?: (entity: any) => void;
  onEdit?: (entity: any) => void;
  onDelete?: (entity: any) => void;
  customActions?: CustomAction[];
  getEntity: (row: number) => any;
}

/**
 * Creates a renderer for the Actions column with View/Edit/Delete buttons
 */
export const createActionsRenderer = (options: ActionsRendererOptions) => {
  const {
    entityName,
    allowUpdate,
    allowDelete,
    onView,
    onEdit,
    onDelete,
    customActions,
    getEntity
  } = options;

  return function(
    this: any,
    instance: Handsontable.Core,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: any,
    cellProperties: Handsontable.CellProperties
  ): HTMLTableCellElement {
    td.innerHTML = '';
    td.className = 'actions-cell htMiddle htCenter';

    const entity = getEntity(row);
    if (!entity) return td;

    const container = document.createElement('div');
    container.className = 'action-buttons';
    container.style.cssText = 'display: flex; flex-direction: column; gap: 4px; align-items: center;';

    // On touch, HOT's touch handler preventDefaults touchstart and swallows
    // the compat click — so each button wires ontouchend to call the action
    // directly. Mouse path is unaffected: onclick fires normally.

    // View button (always show if onView provided)
    if (onView) {
      const viewBtn = document.createElement('button');
      viewBtn.className = 'btn btn-sm btn-outline';
      viewBtn.textContent = 'View';
      viewBtn.title = `View ${entityName}`;
      viewBtn.ontouchend = (e) => { e.preventDefault(); onView(entity); };
      viewBtn.onclick = (e) => {
        e.stopPropagation();
        onView(entity);
      };
      container.appendChild(viewBtn);
    }

    // Edit button (only if allowUpdate and onEdit provided)
    if (allowUpdate && onEdit) {
      const editBtn = document.createElement('button');
      editBtn.className = 'btn btn-sm btn-outline btn-primary';
      editBtn.textContent = 'Edit';
      editBtn.title = `Edit ${entityName}`;
      editBtn.ontouchend = (e) => { e.preventDefault(); onEdit(entity); };
      editBtn.onclick = (e) => {
        e.stopPropagation();
        onEdit(entity);
      };
      container.appendChild(editBtn);
    }

    // Delete button (only if allowDelete and onDelete provided)
    if (allowDelete && onDelete) {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'btn btn-sm btn-outline btn-danger';
      deleteBtn.textContent = 'Delete';
      deleteBtn.title = `Delete ${entityName}`;
      deleteBtn.ontouchend = (e) => { e.preventDefault(); onDelete(entity); };
      deleteBtn.onclick = (e) => {
        e.stopPropagation();
        onDelete(entity);
      };
      container.appendChild(deleteBtn);
    }

    // Custom actions
    if (customActions && customActions.length > 0) {
      customActions.forEach(action => {
        const isVisible = action.isVisible ? action.isVisible(entity) : true;
        if (!isVisible) return;

        const isEnabled = action.isEnabled ? action.isEnabled(entity) : true;

        const actionBtn = document.createElement('button');
        actionBtn.className = action.className || 'btn btn-sm btn-outline';
        actionBtn.title = action.label;
        actionBtn.disabled = !isEnabled;

        if (action.icon) {
          const iconSpan = document.createElement('span');
          iconSpan.style.marginRight = '4px';
          iconSpan.innerHTML = action.icon;
          actionBtn.appendChild(iconSpan);
        }

        const labelSpan = document.createTextNode(action.label);
        actionBtn.appendChild(labelSpan);

        actionBtn.ontouchend = (e) => { e.preventDefault(); if (isEnabled) action.onClick(entity); };
        actionBtn.onclick = (e) => {
          e.stopPropagation();
          if (isEnabled) action.onClick(entity);
        };

        container.appendChild(actionBtn);
      });
    }

    td.appendChild(container);
    return td;
  };
};

/**
 * Renders a column header in the HOT-like layout used across all explorers:
 *
 *   [ Title ↑                              ⏵ ]
 *     ├── click anywhere on the title to sort (cursor:pointer when sortable)
 *     ├── ellipsizes with "..." when the column is too narrow
 *     ├── inline ↑ or ↓ shown only when this column is the active sort
 *     └── filter funnel pinned to the right edge of the cell
 *
 * Click handling stays where it was — HOTDataGrid's afterOnCellMouseDown hook
 * sends the click to onSort unless the target is the .column-filter-icon
 * (which the native DOM listener handles separately).
 */
export const createSortableHeaderRenderer = (
  title: string,
  field: string,
  sortConfig: { field: string; direction: 'asc' | 'desc' } | null,
  sortable: boolean,
  filterable: boolean = false,
  filterActive: boolean = false,
  filterField?: string
) => {
  const isSorted = sortConfig?.field === field;
  const direction = isSorted ? sortConfig.direction : null;

  // Inline arrow only when this column is the active sort. Unsorted columns
  // show nothing — the hover state on .column-header-text hints sortability.
  let sortArrow = '';
  if (direction === 'asc') {
    sortArrow = `<span class="sort-indicator asc" data-sort-field="${field}" data-testid="sort-${field}" aria-label="Sorted ascending">↑</span>`;
  } else if (direction === 'desc') {
    sortArrow = `<span class="sort-indicator desc" data-sort-field="${field}" data-testid="sort-${field}" aria-label="Sorted descending">↓</span>`;
  }

  const titleClass = `column-header-text${sortable ? ' sortable' : ''}`;
  const titleAria = sortable ? `role="button" aria-label="Sort by ${title}" data-sort-field="${field}"` : '';
  // Title + sort arrow are wrapped in a left-group so they always stay
  // packed together at the left of the cell. The outer flex uses
  // justify-content:space-between to push the filter funnel to the right.
  const titleSpan = `<span class="${titleClass}" ${titleAria}>${title}</span>`;
  const leftGroup = `<span class="hdr-left-group">${titleSpan}${sortArrow}</span>`;

  let filterIcon = '';
  if (filterable) {
    const activeClass = filterActive ? ' filter-active' : '';
    const resolvedField = filterField || field;
    const filterLabel = filterActive ? `Clear filter on ${title}` : `Filter ${title}`;
    filterIcon = `<span class="column-filter-icon${activeClass}" role="button" aria-label="${filterLabel}" data-filter-field="${resolvedField}" data-testid="filter-${resolvedField}" tabindex="0"><i class="fa fa-filter"></i></span>`;
  }

  return `<div class="col-header-flex">${leftGroup}${filterIcon}</div>`;
};
